import { EditOutlined, PlusOutlined } from "@ant-design/icons"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { Button } from "components/Button"
import { openNotification } from "components/Message/Message"
import FatButton from "components/Button/FatButton/FatButton"
import { Buttons, Card } from "components/Card/Card"
import { Input } from "components/Input"
import PreviousButton from "components/Previous/PreviousButton"
import { adminOwnerInfoReset, adminOwnerInfoUpdateRequest } from "containers/Admin/services/actions"
import {
  CANCEL_PROFESSIONAL_TO_INSERT,
  CARERS_CLEAR_MESSAGE,
  GET_IDENTITY_FROM_RPPS_REQUEST,
  RPPS_UNKNOWN_CODE,
} from "containers/InsertCarer/services/constants"
import { DirectoryMss, knownMssEmail } from "containers/PharmacySettings/useDirectoryMss"
import { cdn } from "core/cdn"
import { Keyboard, KeyboardReset } from "hocs/withKeyboard"
import { useLanguage } from "locales"
import { Message } from "types/redux"
import { AdminStore, ClientStore, InsertCarerStore, OwnerInfoField } from "types/store"

import styles from "./PharmacyOwnerInfo.module.scss"

// Changing the RPPS means a new owner, so it follows the first setup: RPPS, pharmacist card, FINESS.
// The MSS address is a pharmacy setting edited on the MSS screen, reached through onEditMss.
type BillingField = Exclude<OwnerInfoField, "mss_email">
type Step = "summary" | BillingField | "rpps_finess" | "rpps_confirm"

type InputStep = Exclude<Step, "summary" | "rpps_confirm">

// Field edited by an input step: the FINESS of the RPPS sequence is still the FINESS
const fieldOf = (step: InputStep): BillingField => (step === "rpps_finess" ? "finess" : step)

interface NewOwner {
  rpps: string
  firstname?: string
  lastname?: string
  profession?: string
}

const FIELDS: OwnerInfoField[] = ["rpps", "finess", "mss_email"]

const KEYBOARD_BY_FIELD: Record<BillingField, { type: string; maxLength?: number }> = {
  rpps: { type: "rpps", maxLength: 11 },
  finess: { type: "finess", maxLength: 9 },
}

// Rendered inside the "Espace professionnel" modal, after the téléexpertise menu
const PharmacyOwnerInfo: React.FC<{
  directory: DirectoryMss
  onBack: () => void
  // The MSS line opens the MSS settings screen
  onEditMss: () => void
  // A new owner has just been saved: the parent continues with the MSS address of the new RPPS
  onOwnerChanged: () => void
  // Message to show on arrival, such as the save confirmed on the MSS screen
  initialNotice?: Message | null
}> = ({ directory, onBack, onEditMss, onOwnerChanged, initialNotice = null }) => {
  const dispatch = useDispatch()
  const { languages } = useLanguage()

  const admin = useSelector(({ client }: { client: ClientStore }) => client.admin)
  const ownerInfo = useSelector(
    ({ admin }: { admin: Partial<AdminStore> }) => admin.ownerInfo,
  )

  const [step, setStep] = useState<Step>("summary")
  const [value, setValue] = useState("")
  const [inputError, setInputError] = useState<Message | null>(null)
  const [notice, setNotice] = useState<Message | null>(initialNotice)
  const [newOwner, setNewOwner] = useState<NewOwner | null>(null)
  const [awaitingIdentity, setAwaitingIdentity] = useState(false)
  // True while the save of a new owner (RPPS sequence) is running
  const savingNewOwner = useRef(false)
  // Only a save started from this screen may change it: the status found at mount may belong
  // to a save made on the MSS screen just before
  const saveStarted = useRef(false)
  const entryKeyboard = useRef<Keyboard>(null)

  const professionalToInsert = useSelector(
    ({ carer }: { carer: Partial<InsertCarerStore> }) => carer.professionalToInsert,
  )
  const carerLoading = useSelector(({ carer }: { carer: Partial<InsertCarerStore> }) => carer.loading)
  const carerMessage = useSelector(({ carer }: { carer: Partial<InsertCarerStore> }) => carer.message)
  // An RPPS the directory does not know gets the wording of the screen, not the generic one.
  // Built once per carer message: the card notifies again for every new message object
  const rppsMessage = useMemo(
    () =>
      carerMessage?.code === RPPS_UNKNOWN_CODE
        ? { type: "error" as const, text: languages.ownerInfoRppsNotRecognized }
        : carerMessage,
    [carerMessage, languages],
  )

  // RPPS refused by the directory: stop waiting for the pharmacist card
  useEffect(() => {
    if (carerMessage?.type === "error") setAwaitingIdentity(false)
  }, [carerMessage])

  // The keyboard keeps its own copy of the text and is not seeded with a prefilled value:
  // align it with the field on every entry step, so typing continues a prefilled RPPS or FINESS
  // instead of replacing it
  useEffect(() => {
    const isEntryStep = step !== "summary" && step !== "rpps_confirm"
    if (isEntryStep) entryKeyboard.current?.keyboard?.setInput(value)
  }, [step, value])

  // The MSS line shows the entered address, otherwise the one found in the directory
  const shownValue = (field: OwnerInfoField): string | undefined =>
    field === "mss_email" ? knownMssEmail(admin, directory) : admin?.[field]

  useEffect(() => {
    dispatch(adminOwnerInfoReset())
    return () => {
      dispatch(adminOwnerInfoReset())
      dispatch({ type: CANCEL_PROFESSIONAL_TO_INSERT })
    }
  }, [dispatch])

  // RPPS recognised by the directory: show the pharmacist card
  useEffect(() => {
    if (!awaitingIdentity || !professionalToInsert) return
    setAwaitingIdentity(false)
    setNewOwner({
      rpps: professionalToInsert.rpps ?? value.trim(),
      firstname: professionalToInsert.firstname,
      lastname: professionalToInsert.lastname,
      profession: professionalToInsert.profession,
    })
    setStep("rpps_confirm")
    setInputError(null)
    setNotice(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professionalToInsert, awaitingIdentity])

  useEffect(() => {
    if (ownerInfo?.status === "success" || ownerInfo?.status === "error") {
      if (!saveStarted.current) return
      saveStarted.current = false
    }
    if (ownerInfo?.status === "success") {
      setNewOwner(null)
      setStep("summary")
      setNotice(ownerInfo.message ?? null)
      if (savingNewOwner.current) {
        savingNewOwner.current = false
        if (ownerInfo.message) openNotification(ownerInfo.message)
        onOwnerChanged()
      }
    }
    if (ownerInfo?.status === "error") savingNewOwner.current = false
    if (ownerInfo?.status === "incomplete" && ownerInfo.missing) {
      // The billing record needs both RPPS and FINESS: ask for the missing one next.
      // A missing RPPS starts the full RPPS sequence.
      if (ownerInfo.missing === "rpps") startRppsSequence()
      else if (ownerInfo.missing === "finess") setStep("finess")
      setValue("")
      setInputError(null)
      setNotice({
        type: "info",
        text:
          ownerInfo.missing === "rpps"
            ? languages.ownerInfoMissingRpps
            : languages.ownerInfoMissingFiness,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerInfo?.status, ownerInfo?.missing])

  const labels: Record<OwnerInfoField, string> = {
    rpps: languages.ownerInfoRppsLabel,
    finess: languages.ownerInfoFinessLabel,
    mss_email: languages.ownerInfoMssLabel,
  }

  function startRppsSequence() {
    dispatch({ type: CANCEL_PROFESSIONAL_TO_INSERT })
    dispatch({ type: CARERS_CLEAR_MESSAGE })
    setAwaitingIdentity(false)
    setNewOwner(null)
    setStep("rpps")
  }

  const openField = (field: OwnerInfoField) => {
    if (field === "mss_email") {
      onEditMss()
      return
    }
    dispatch(adminOwnerInfoReset())
    if (field === "rpps") startRppsSequence()
    else setStep(field)
    // Start from the current value, editable right away (the cross clears it)
    setValue(shownValue(field) ?? "")
    setInputError(null)
    setNotice(null)
  }

  const backToSummary = () => {
    dispatch(adminOwnerInfoReset())
    dispatch({ type: CANCEL_PROFESSIONAL_TO_INSERT })
    setNewOwner(null)
    setAwaitingIdentity(false)
    setStep("summary")
    setValue("")
    setInputError(null)
    setNotice(null)
  }

  // "Retour" goes one step back inside the RPPS sequence, otherwise back to the summary
  const goBack = () => {
    setInputError(null)
    setNotice(null)
    if (step === "rpps_finess") {
      setStep("rpps_confirm")
      return
    }
    if (step === "rpps_confirm") {
      dispatch({ type: CANCEL_PROFESSIONAL_TO_INSERT })
      dispatch({ type: CARERS_CLEAR_MESSAGE })
      setStep("rpps")
      setValue(newOwner?.rpps ?? "")
      setNewOwner(null)
      return
    }
    backToSummary()
  }

  const submit = () => {
    if (step === "summary" || step === "rpps_confirm" || ownerInfo?.loading || carerLoading) return
    const trimmed = value.trim()
    if (!trimmed) return
    const field = fieldOf(step)
    if (field === "rpps" && !/^\d{1,11}$/.test(trimmed)) {
      setInputError({ type: "error", text: languages.pharmacistRppsLengthError })
      return
    }
    if (field === "finess" && !/^\d{9}$/.test(trimmed)) {
      setInputError({ type: "error", text: languages.pharmacistFinessLengthError })
      return
    }
    setInputError(null)
    setNotice(null)
    if (step === "rpps") {
      // Verify the RPPS with the directory before asking for the FINESS
      dispatch({ type: CARERS_CLEAR_MESSAGE })
      dispatch({ type: CANCEL_PROFESSIONAL_TO_INSERT })
      setAwaitingIdentity(true)
      dispatch({ type: GET_IDENTITY_FROM_RPPS_REQUEST, payload: { rpps: trimmed } })
      return
    }
    if (step === "rpps_finess") {
      // Last step of the RPPS sequence: save the new owner with the FINESS
      if (!newOwner) return
      savingNewOwner.current = true
      saveStarted.current = true
      dispatch(
        adminOwnerInfoUpdateRequest({
          ...(ownerInfo?.draft ?? {}),
          rpps: newOwner.rpps,
          finess: trimmed,
          owner_firstname: newOwner.firstname ?? "",
          owner_lastname: newOwner.lastname ?? "",
        }),
      )
      return
    }
    saveStarted.current = true
    dispatch(adminOwnerInfoUpdateRequest({ ...(ownerInfo?.draft ?? {}), [step]: trimmed }))
  }

  const continueToFiness = () => {
    setStep("rpps_finess")
    setValue(ownerInfo?.draft?.finess ?? "")
    setInputError(null)
    setNotice({ type: "success", text: languages.ownerInfoSaved })
  }

  if (step === "summary") {
    const summaryMessage =
      notice ?? (ownerInfo?.status === "error" ? ownerInfo.message : undefined) ?? undefined
    return (
      <>
        <PreviousButton onClick={onBack} />
        <Card message={summaryMessage}>
          <h1 className={styles.Title}>{languages.ownerInfoTitle}</h1>
          <div className={styles.Rows}>
            {FIELDS.map((field) => {
              const current = shownValue(field)
              return (
                <div key={field} className={styles.Row}>
                  <span className={styles.Label}>{labels[field]}</span>
                  <span className={current ? styles.Value : `${styles.Value} ${styles.Empty}`}>
                    {current || languages.not_informed_singular}
                  </span>
                  <Button
                    className={styles.RowButton}
                    icon={current ? <EditOutlined /> : <PlusOutlined />}
                    // The MSS line waits for the directory answer before it can be edited
                    disabled={field === "mss_email" && directory.loading}
                    onClick={() => openField(field)}
                  >
                    {(current ? languages.modify : languages.add).toUpperCase()}
                  </Button>
                </div>
              )
            })}
          </div>
        </Card>
      </>
    )
  }

  if (step === "rpps_confirm") {
    return (
      <>
        <PreviousButton onClick={goBack} />
        <Card
          title={languages.pharmacistInfoTitle}
          titleCustomStyle={{ margin: "20px 0 40px" }}
        >
          <div className="flex-column flex-center">
            {/* Same card as the first setup, growing with a long name so the RPPS stays inside */}
            <FatButton disabled style={{ height: "auto", minHeight: 250 }}>
              <div className={styles.OwnerCard}>
                <img src={cdn("/images/ps_picto.svg")} alt="" />
                <b>
                  {newOwner?.firstname} {newOwner?.lastname}
                </b>
                <i>{newOwner?.profession ?? languages.healthPro}</i>
                <span className={styles.OwnerCardDetail}>
                  {languages.ownerInfoRppsLabel} {newOwner?.rpps}
                </span>
              </div>
            </FatButton>
            <Buttons>
              <Button wide="long" onClick={goBack}>
                {languages.cancel}
              </Button>
              <Button wide="long" type="primary" onClick={continueToFiness}>
                {languages.pharmacistInfoContinue}
              </Button>
            </Buttons>
          </div>
        </Card>
      </>
    )
  }

  const field = fieldOf(step)
  const keyboard = KEYBOARD_BY_FIELD[field]
  const heading =
    field === "rpps" ? (
      <>
        {languages.pharmacistEnterRppsPrefix} <strong>{languages.pharmacistTitulaireLabel}</strong>
      </>
    ) : (
      <>
        <strong>{languages.pharmacistFinessHeadingBold}</strong>{" "}
        {languages.pharmacistFinessHeadingSuffix}
      </>
    )
  const placeholder =
    field === "rpps" ? languages.pharmacistRppsPlaceholder : languages.pharmacistFinessPlaceholder
  const cardMessage =
    inputError ??
    (step === "rpps" ? rppsMessage : undefined) ??
    (ownerInfo?.status === "error" ? ownerInfo.message : undefined) ??
    notice ??
    undefined
  const busy = ownerInfo?.loading || carerLoading
  // Nothing to save while the value shown is unchanged: a new owner must be a different RPPS,
  // and a FINESS correction must change the FINESS (a new owner may keep the same FINESS)
  const unchanged =
    (step === "rpps" && value.trim() === (admin?.rpps ?? "")) ||
    (step === "finess" && value.trim() === (admin?.finess ?? ""))

  return (
    <>
      <PreviousButton onClick={goBack} />
      <Card message={cardMessage}>
        <div className={styles.Heading}>{heading}</div>
        {field === "finess" && (
          <div className={styles.Subheading}>{languages.pharmacistFinessFacturationLabel}</div>
        )}
        <Keyboard
          key={step}
          ref={entryKeyboard}
          // The popup has a fixed size: drop the shared keyboard's minimum height here
          containerStyle={{ minHeight: 0 }}
          options={{ type: keyboard.type, inputName: field }}
          value={value}
          onChange={(val: string) => {
            // The keyboard caps the field only after its first key press and stores the full value
            // right after this callback: cap here, then realign its own copy so backspace never
            // erases an invisible extra digit
            const capped = keyboard.maxLength ? val.slice(0, keyboard.maxLength) : val
            if (capped !== val) {
              queueMicrotask(() => entryKeyboard.current?.onUserKeyboardChange({ [field]: capped }))
            }
            setValue(capped)
            setInputError(null)
          }}
        >
          <div className={styles.InputWrapper}>
            <Input
              placeholder={placeholder}
              value={value}
              name={field}
              maxLength={keyboard.maxLength}
            />
            {/* The keyboard draws its own clear button, except for FINESS */}
            {field === "finess" && value ? (
              <KeyboardReset
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setValue("")
                  setInputError(null)
                }}
              />
            ) : null}
          </div>
        </Keyboard>
        <Buttons>
          <Button
            type="primary"
            className={styles.ConfirmButton}
            onClick={submit}
            loading={busy}
            disabled={!value.trim() || busy || unchanged}
          >
            {(step === "rpps" ? languages.next : languages.confirm).toUpperCase()}
          </Button>
        </Buttons>
      </Card>
    </>
  )
}

export default PharmacyOwnerInfo
